import { useState, useMemo, useEffect } from 'react'
import { usePagination } from './usePagination'

interface UseNoticiasSearchProps {
  noticias: any[]
  itemsPerPage?: number
}

export function useNoticiasSearch({ 
  noticias, 
  itemsPerPage = 6 
}: UseNoticiasSearchProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [searchFilters, setSearchFilters] = useState({
    type: '',
    dateRange: null as { start: Date; end: Date } | null
  })

  // Filtrar notícias baseado no termo de busca
  const filteredNoticias = useMemo(() => {
    if (!noticias || noticias.length === 0) return []
    
    let filtered = noticias

    // Filtro por termo de busca
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase()
      filtered = filtered.filter((noticia: any) => {
        const title = noticia.header?.title?.toLowerCase() || ''
        const description = noticia.header?.description?.toLowerCase() || ''
        const type = noticia.header?.type?.toLowerCase() || ''
        const autor = noticia.noticiaPage?.autor?.toLowerCase() || ''
        
        return title.includes(searchLower) || 
               description.includes(searchLower) || 
               type.includes(searchLower) ||
               autor.includes(searchLower)
      })
    }

    // Filtro por tipo
    if (searchFilters.type) {
      filtered = filtered.filter((noticia: any) => 
        noticia.header?.type?.toLowerCase() === searchFilters.type.toLowerCase()
      )
    }

    // Filtro por data (se implementado)
    if (searchFilters.dateRange) {
      filtered = filtered.filter((noticia: any) => {
        const noticiaDate = new Date(noticia.dataAtualizacao || noticia.updatedAt)
        return noticiaDate >= searchFilters.dateRange!.start && 
               noticiaDate <= searchFilters.dateRange!.end
      })
    }

    // Ordenar por data de atualização (mais recente primeiro)
    filtered.sort((a: any, b: any) => {
      const dateA = new Date(a.dataAtualizacao || a.updatedAt || 0)
      const dateB = new Date(b.dataAtualizacao || b.updatedAt || 0)
      return dateB.getTime() - dateA.getTime() // Ordem decrescente (mais recente primeiro)
    })

    return filtered
  }, [noticias, searchTerm, searchFilters])

  // Paginação das notícias filtradas
  const pagination = usePagination({
    items: filteredNoticias,
    itemsPerPage
  })

  // Resetar para a primeira página quando a busca mudar
  useEffect(() => {
    pagination.resetToFirstPage()
  }, [searchTerm, searchFilters, pagination])

  // Obter tipos únicos para filtro
  const uniqueTypes = useMemo(() => {
    if (!noticias) return []
    
    const types = noticias
      .map((noticia: any) => noticia.header?.type)
      .filter((type: string) => type && type.trim() !== '')
    
    return Array.from(new Set(types))
  }, [noticias])

  const clearSearch = () => {
    setSearchTerm('')
    setSearchFilters({
      type: '',
      dateRange: null
    })
  }

  const clearFilters = () => {
    setSearchFilters({
      type: '',
      dateRange: null
    })
  }

  return {
    // Busca
    searchTerm,
    setSearchTerm,
    searchFilters,
    setSearchFilters,
    
    // Notícias filtradas e paginadas
    filteredNoticias: pagination.currentItems,
    allFilteredNoticias: filteredNoticias, // Todas as notícias filtradas (para contador)
    
    // Paginação
    pagination,
    
    // Filtros
    uniqueTypes,
    clearSearch,
    clearFilters,
    hasActiveFilters: searchTerm.trim() !== '' || searchFilters.type !== '' || searchFilters.dateRange !== null
  }
} 